import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Underline } from '@tiptap/extension-underline';
import FontSize from '@tiptap/extension-font-size';
import FontFamily from '@tiptap/extension-font-family';
import { Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify, Highlighter, Type, Link as LinkIcon, List, ListOrdered, Undo2, Redo2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import FONT_FAMILIES from '../constants/fonts';
import AiTextAssistant from './AiTextAssistant';
import ColorPickerSwatch from './ColorPickerSwatch';

const TipTapEditor = ({ content, onChange, placeholder = 'Start typing...', enableAiAssist = false }) => {
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  // Force re-render whenever the editor's selection changes so toolbar
  // controls (font size, font family, heading, alignment, etc.) reflect
  // the attributes at the current cursor / selection. TipTap v3's
  // useEditor does NOT re-render on selection changes by default.
  const [, forceToolbarUpdate] = React.useReducer((x) => x + 1, 0);
  // Store selection state before opening dropdowns
  const savedSelectionRef = React.useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        // Exclude extensions that are added explicitly below to prevent
        // the "[tiptap warn]: Duplicate extension names found" warning
        underline: false,
        link: false,
      }),
      Underline,
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #4F46E5; text-decoration: underline;'
        }
      }),
      Image,
    ],
    content: content || '<p></p>',
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    onTransaction: () => {
      // Re-render the toolbar on every editor transaction so font-size,
      // font-family, heading style and alignment dropdowns reflect the
      // current selection. TipTap v3's useEditor does not do this by default.
      forceToolbarUpdate();
    },
  });

  // Sync external content changes into the editor (e.g. when a parent list
  // reorders its items). Guarded by a diff check so it never fires during
  // normal typing (onChange always mirrors getHTML), avoiding cursor jumps.
  React.useEffect(() => {
    if (!editor) return;
    const incoming = content || '<p></p>';
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [content, editor]);
  
  // Popover state controls
  const [textColorOpen, setTextColorOpen] = React.useState(false);
  const [highlightOpen, setHighlightOpen] = React.useState(false);
  
  // Save current selection before dropdown opens
  const saveSelection = () => {
    if (editor) {
      const from = editor.state.selection.from;
      const to = editor.state.selection.to;
      savedSelectionRef.current = { from, to };
    }
  };
  
  // Restore selection and apply command using a single chain
  const restoreAndRun = (command) => {
    if (!editor) return;
    
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current;
      // Focus and restore selection first
      editor.chain().focus().setTextSelection({ from, to }).run();
    } else {
      editor.chain().focus().run();
    }
    
    // Execute the command after a microtask to ensure selection is set
    Promise.resolve().then(() => {
      command();
    });
  };
  
  // Direct highlight function for better reliability
  const applyHighlight = (color) => {
    if (!editor) {
      console.log('[Highlight] No editor');
      return;
    }
    
    console.log('[Highlight] Applying color:', color);
    console.log('[Highlight] Saved selection (ref):', savedSelectionRef.current);
    
    // Use ref since it's more reliable across renders
    const sel = savedSelectionRef.current;
    
    if (sel && sel.from !== sel.to) {
      console.log('[Highlight] Using saved selection:', sel.from, sel.to);
      editor.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).setHighlight({ color }).run();
    } else {
      console.log('[Highlight] No valid selection');
    }
    setHighlightOpen(false);
  };
  
  const removeHighlight = () => {
    if (!editor) return;
    
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current;
      editor.chain().focus().setTextSelection({ from, to }).unsetHighlight().run();
    } else {
      editor.chain().focus().unsetHighlight().run();
    }
    setHighlightOpen(false);
  };
  
  // Direct text color function for better reliability
  const applyTextColor = (color) => {
    if (!editor) return;
    
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current;
      editor.chain().focus().setTextSelection({ from, to }).setColor(color).run();
    } else {
      editor.chain().focus().setColor(color).run();
    }
    setTextColorOpen(false);
  };
  
  const removeTextColor = () => {
    if (!editor) return;
    
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current;
      editor.chain().focus().setTextSelection({ from, to }).unsetColor().run();
    } else {
      editor.chain().focus().unsetColor().run();
    }
    setTextColorOpen(false);
  };

  const addLink = () => {
    if (linkUrl && editor) {
      // Restore selection before applying link
      if (savedSelectionRef.current) {
        const { from, to } = savedSelectionRef.current;
        editor.chain().focus().setTextSelection({ from, to }).setLink({ href: linkUrl }).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  if (!editor) {
    return null;
  }

  const textColor = editor.getAttributes('textStyle').color || '#000000';
  const highlightColor = editor.getAttributes('highlight').color || '#ffff00';
  
  // Get current heading level
  const getCurrentHeading = () => {
    if (editor.isActive('heading', { level: 1 })) return 'h1';
    if (editor.isActive('heading', { level: 2 })) return 'h2';
    if (editor.isActive('heading', { level: 3 })) return 'h3';
    if (editor.isActive('heading', { level: 4 })) return 'h4';
    if (editor.isActive('heading', { level: 5 })) return 'h5';
    if (editor.isActive('heading', { level: 6 })) return 'h6';
    return 'p';
  };

  // Get current font size. Order of preference:
  //   1. Explicit textStyle.fontSize at the cursor (set via the size dropdown)
  //   2. Inferred default for the active heading level (h1 -> 32px, etc.)
  //   3. 16px fallback for body paragraphs.
  // This stops the dropdown from incorrectly showing 16px (or whichever size
  // was last applied) when the cursor is inside an unstyled heading.
  const getCurrentFontSize = () => {
    if (!editor) return '16px';
    const explicit = editor.getAttributes('textStyle').fontSize;
    if (explicit) return explicit;
    // Heading default sizes - intentionally aligned with our render CSS
    const headingDefaults = {
      1: '32px',
      2: '24px',
      3: '20px',
      4: '18px',
      5: '16px',
      6: '14px',
    };
    for (let level = 1; level <= 6; level += 1) {
      if (editor.isActive('heading', { level })) {
        return headingDefaults[level];
      }
    }
    return '16px';
  };

  // Font size presets
  const fontSizes = [
    { label: '6px', value: '6px' },
    { label: '8px', value: '8px' },
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px', value: '16px' },
    { label: '18px', value: '18px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
    { label: '28px', value: '28px' },
    { label: '32px', value: '32px' },
    { label: '36px', value: '36px' },
    { label: '40px', value: '40px' },
    { label: '48px', value: '48px' },
    { label: '56px', value: '56px' },
    { label: '64px', value: '64px' },
    { label: '72px', value: '72px' },
  ];

  // Get current font family
  const getCurrentFontFamily = () => {
    const attrs = editor.getAttributes('textStyle');
    return attrs.fontFamily || 'inherit';
  };

  return (
    <div className="border border-slate-200 rounded-lg">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-3 bg-slate-50 border-b border-slate-200">
        
        {/* Undo / Redo */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="p-2 hover:bg-slate-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo (Ctrl/Cmd+Z)"
            data-testid="tiptap-undo-btn"
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="p-2 hover:bg-slate-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo (Ctrl/Cmd+Shift+Z)"
            data-testid="tiptap-redo-btn"
          >
            <Redo2 size={16} />
          </button>
        </div>
        
        {/* Heading Selector */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Select
            value={getCurrentHeading()}
            onOpenChange={(open) => {
              if (open) saveSelection();
            }}
            onValueChange={(value) => {
              restoreAndRun(() => {
                if (value === 'p') {
                  editor.chain().focus().setParagraph().run();
                } else {
                  const level = parseInt(value.replace('h', ''));
                  editor.chain().focus().setHeading({ level }).run();
                }
              });
            }}
          >
            <SelectTrigger className="h-8 w-24 text-xs" data-testid="tiptap-heading-select">
              <SelectValue placeholder="Style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p">Paragraph</SelectItem>
              <SelectItem value="h1">Heading 1</SelectItem>
              <SelectItem value="h2">Heading 2</SelectItem>
              <SelectItem value="h3">Heading 3</SelectItem>
              <SelectItem value="h4">Heading 4</SelectItem>
              <SelectItem value="h5">Heading 5</SelectItem>
              <SelectItem value="h6">Heading 6</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Font Size Selector */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Select
            value={getCurrentFontSize()}
            onOpenChange={(open) => {
              if (open) saveSelection();
            }}
            onValueChange={(value) => {
              restoreAndRun(() => {
                editor.chain().focus().setFontSize(value).run();
              });
            }}
          >
            <SelectTrigger className="h-8 w-20 text-xs" data-testid="tiptap-fontsize-select">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              {fontSizes.map((size) => (
                <SelectItem key={size.value} value={size.value}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Font Family selector */}
          <Select
            value={getCurrentFontFamily()}
            onOpenChange={(open) => {
              if (open) saveSelection();
            }}
            onValueChange={(value) => {
              restoreAndRun(() => {
                if (value === 'inherit') {
                  editor.chain().focus().unsetFontFamily().run();
                } else {
                  editor.chain().focus().setFontFamily(value).run();
                }
              });
            }}
          >
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="tiptap-fontfamily-select">
              <Type size={14} className="mr-1" />
              <SelectValue placeholder="Font" />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((font) => (
                <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                  {font.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Text Formatting */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-bold"
          >
            <Bold size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-italic"
          >
            <Italic size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-underline"
          >
            <UnderlineIcon size={16} />
          </Button>
        </div>

        {/* Colors */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Popover open={textColorOpen} onOpenChange={(open) => { 
            setTextColorOpen(open);
            if (open) saveSelection(); 
          }}>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 relative" 
                data-testid="tiptap-text-color"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                }}
              >
                <Type size={16} />
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-1 rounded" style={{ backgroundColor: textColor }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" onOpenAutoFocus={(e) => e.preventDefault()} onFocusOutside={(e) => e.preventDefault()}>
              <div className="space-y-2">
                <label className="text-sm font-medium">Text Color</label>
                <div className="grid grid-cols-8 gap-1">
                  {['#000000', '#ffffff', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'].map((color) => (
                    <button
                      key={color}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const sel = savedSelectionRef.current;
                        if (editor && sel && sel.from !== sel.to) {
                          editor.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).setColor(color).run();
                        }
                        setTextColorOpen(false);
                      }}
                      className="w-6 h-6 rounded border border-slate-300 hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                {/* Full colour picker — matches the native wheel used by
                    button/background colour controls elsewhere in the CMS.
                    Uses `onInput` so the selection updates live as the user
                    drags across the wheel; popover stays open so they can
                    fine-tune. Falls back to the hex text field for exact
                    values. */}
                <div className="pt-1 border-t border-slate-100">
                  <label className="text-[11px] text-slate-500 block mb-1">Custom colour</label>
                  <div className="flex items-center gap-2">
                    <ColorPickerSwatch
                      value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(textColor) ? textColor : '#000000'}
                      onPickerMouseDown={() => saveSelection()}
                      onChange={(e) => {
                        const color = e.target.value;
                        const sel = savedSelectionRef.current;
                        if (editor && sel && sel.from !== sel.to) {
                          // NO .focus() — prevents Popover closing via onFocusOutside
                          editor.chain().setTextSelection({ from: sel.from, to: sel.to }).setColor(color).run();
                        } else if (editor) {
                          editor.chain().setColor(color).run();
                        }
                      }}
                      data-testid="tiptap-text-color-picker"
                    />
                    <input
                      type="text"
                      value={textColor}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
                          const sel = savedSelectionRef.current;
                          if (editor && sel && sel.from !== sel.to) {
                            editor.chain().setTextSelection({ from: sel.from, to: sel.to }).setColor(raw).run();
                          } else if (editor) {
                            editor.chain().setColor(raw).run();
                          }
                        }
                      }}
                      className="flex-1 h-7 text-xs font-mono px-2 rounded border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      placeholder="#000000"
                      data-testid="tiptap-text-color-hex"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const sel = savedSelectionRef.current;
                    if (editor && sel && sel.from !== sel.to) {
                      editor.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).unsetColor().run();
                    }
                    setTextColorOpen(false);
                  }}
                  className="w-full"
                >
                  Clear Color
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={highlightOpen} onOpenChange={(open) => { 
            setHighlightOpen(open);
            if (open) saveSelection(); 
          }}>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 relative" 
                data-testid="tiptap-highlight"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                }}
              >
                <Highlighter size={16} />
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-1 rounded" style={{ backgroundColor: highlightColor }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" onOpenAutoFocus={(e) => e.preventDefault()} onFocusOutside={(e) => e.preventDefault()}>
              <div className="space-y-2">
                <label className="text-sm font-medium">Highlight Color</label>
                <div className="grid grid-cols-8 gap-1">
                  {['#ffff00', '#fef3c7', '#fecaca', '#bbf7d0', '#bfdbfe', '#ddd6fe', '#fce7f3', '#f1f5f9'].map((color) => (
                    <button
                      key={color}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const sel = savedSelectionRef.current;
                        if (editor && sel) {
                          if (sel.from !== sel.to) {
                            editor.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).setHighlight({ color }).run();
                          } else {
                            editor.chain().focus().setHighlight({ color }).run();
                          }
                        }
                        setHighlightOpen(false);
                      }}
                      className="w-6 h-6 rounded border border-slate-300 hover:scale-110 transition-transform cursor-pointer"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="pt-1 border-t border-slate-100">
                  <label className="text-[11px] text-slate-500 block mb-1">Custom colour</label>
                  <div className="flex items-center gap-2">
                    <ColorPickerSwatch
                      value={highlightColor || '#ffff00'}
                      onPickerMouseDown={() => saveSelection()}
                      onChange={(e) => {
                        const color = e.target.value;
                        const sel = savedSelectionRef.current;
                        if (editor && sel && sel.from !== sel.to) {
                          // NO .focus() — prevents Popover closing via onFocusOutside
                          editor.chain().setTextSelection({ from: sel.from, to: sel.to }).setHighlight({ color }).run();
                        } else if (editor) {
                          editor.chain().setHighlight({ color }).run();
                        }
                      }}
                      data-testid="tiptap-highlight-color-picker"
                    />
                    <input
                      type="text"
                      value={highlightColor || '#ffff00'}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
                          const sel = savedSelectionRef.current;
                          if (editor && sel && sel.from !== sel.to) {
                            editor.chain().setTextSelection({ from: sel.from, to: sel.to }).setHighlight({ color: raw }).run();
                          } else if (editor) {
                            editor.chain().setHighlight({ color: raw }).run();
                          }
                        }
                      }}
                      className="flex-1 h-7 text-xs font-mono px-2 rounded border border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      placeholder="#ffff00"
                      data-testid="tiptap-highlight-color-hex"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const sel = savedSelectionRef.current;
                    if (editor && sel && sel.from !== sel.to) {
                      editor.chain().focus().setTextSelection({ from: sel.from, to: sel.to }).unsetHighlight().run();
                    }
                    setHighlightOpen(false);
                  }}
                  className="w-full"
                >
                  Remove Highlight
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'left' }) ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-align-left"
          >
            <AlignLeft size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'center' }) ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-align-center"
          >
            <AlignCenter size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'right' }) ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-align-right"
          >
            <AlignRight size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'justify' }) ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-align-justify"
          >
            <AlignJustify size={16} />
          </Button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 pr-2 border-r border-slate-300">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-bullet-list"
            title="Bullet List"
          >
            <List size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-slate-200' : ''}`}
            data-testid="tiptap-ordered-list"
            title="Numbered List"
          >
            <ListOrdered size={16} />
          </Button>
        </div>

        {/* Link */}
        <div className="flex items-center gap-1">
          <Popover 
            open={showLinkInput} 
            onOpenChange={(open) => {
              if (open) {
                saveSelection();
              } else {
                setLinkUrl('');
              }
              setShowLinkInput(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                }}
                className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-slate-200' : ''}`}
                data-testid="tiptap-link"
              >
                <LinkIcon size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3" onOpenAutoFocus={(e) => e.preventDefault()}>
              <div className="space-y-2">
                <label className="text-sm font-medium">Add Link</label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="h-8 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addLink();
                    }
                  }}
                  data-testid="tiptap-link-input"
                />
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addLink();
                    }}
                    className="h-8 flex-1 bg-indigo-600 hover:bg-indigo-700"
                  >
                    Add Link
                  </Button>
                  {editor.isActive('link') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        restoreAndRun(() => {
                          editor.chain().focus().unsetLink().run();
                        });
                        setShowLinkInput(false);
                      }}
                      className="h-8"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* AI Writing Assistant */}
        {enableAiAssist && (
          <div className="flex items-center gap-1 pl-2 border-l border-slate-300 ml-auto">
            <AiTextAssistant
              onInsert={(html) => {
                editor.chain().focus().insertContent(html).run();
              }}
            />
          </div>
        )}
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-4 min-h-[350px] focus:outline-none"
        data-testid="tiptap-content"
      />

      <style>{`
        .ProseMirror {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
        }
        .ProseMirror h1 {
          font-size: 36px;
          font-weight: 700;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h2 {
          font-size: 30px;
          font-weight: 700;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h3 {
          font-size: 24px;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h4 {
          font-size: 20px;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h5 {
          font-size: 18px;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h6 {
          font-size: 16px;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror ul li,
        .ProseMirror ol li {
          margin: 0.25rem 0;
        }
        .ProseMirror ul ul {
          list-style-type: circle;
        }
        .ProseMirror ul ul ul {
          list-style-type: square;
        }
      `}</style>
    </div>
  );
};

export default TipTapEditor;
